import { catalogApiFetch } from '../admin/jscontrol/apiClient';
import { getImageBaseUrl, getImageSrc } from './appConfig';
import { fetchProductRatingSummary } from './productReviewApi';
import {
  isColorAttributeName,
  mapColorSwatchesFromAttributeValues,
  parseColorOptionsFromAttributeValues,
} from './colorUtils';
import { resolveProductVariantId } from './productVariant';

export { getImageSrc };
export const getImageBase = () => getImageBaseUrl();

export const parseApiList = (body) => {
  if (!body) return [];
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.Data)) return body.Data;
  if (Array.isArray(body?.data?.data)) return body.data.data;
  const root = body?.data ?? body?.Data ?? body;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.Data)) return root.Data;
  return [];
};

export const isActiveProduct = (p) => {
  const active = p?.IsActive ?? p?.isActive;
  if (active === undefined || active === null) return true;
  return active === true || active === 1 || String(active).toLowerCase() === 'true';
};

export const isFeaturedProduct = (p) => {
  const featured = p?.IsFeatured ?? p?.isFeatured;
  const yes = (v) => v === true || v === 1 || String(v).toLowerCase() === 'true';
  return yes(featured) && isActiveProduct(p);
};

export const fetchAllProducts = async () => {
  const params = new URLSearchParams({
    pageIndex: '1',
    pageSize: '200',
    search: '',
    sortColumn: 'ProductId',
    sortBy: 'DESC',
  });
  const { data } = await catalogApiFetch(`/Product/GetAll?${params}`);
  return parseApiList(data).filter(isActiveProduct);
};

/** Product name autocomplete for storefront search. */
export const fetchProductNameAutocomplete = async (search, limit = 10) => {
  const term = String(search || '').trim();
  if (!term) return [];

  const params = new URLSearchParams({
    search: term,
    limit: String(limit),
  });
  const { data } = await catalogApiFetch(`/Product/Autocomplete?${params}`);
  return parseApiList(data);
};

export const fetchFeaturedProducts = async () => {
  const items = await fetchAllProducts();
  const featured = items.filter(isFeaturedProduct);
  return Promise.all(featured.map((p) => mapProductToCard(p)));
};

export const matchesCategory = (p, categoryName) => {
  const cat = String(p?.CategoryName || p?.categoryName || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  const want = String(categoryName).toLowerCase().replace(/\s+/g, ' ').trim();
  return cat === want || cat.replace(/\s/g, '') === want.replace(/\s/g, '');
};

const parseSizeOptions = (items) => {
  const sizes = [];
  const seen = new Set();
  for (const row of items) {
    const name = String(row.ProductAttributeName || row.AttributeName || '').trim();
    if (isColorAttributeName(name) || !/size/i.test(name)) continue;
    const val = String(row.AttributeValue ?? '').trim();
    if (!val) continue;
    const key = val.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    sizes.push(val);
  }
  return sizes;
};

const parseColorOptions = (items) => parseColorOptionsFromAttributeValues(items);

export const fetchProductImages = async (productId) => {
  try {
    const { data } = await catalogApiFetch(`/ProductImage/GetByProductId/${productId}`);
    const items = parseApiList(data);
    return items
      .slice()
      .sort((a, b) => {
        const aPrimary = a.IsPrimary ?? a.isPrimary;
        const bPrimary = b.IsPrimary ?? b.isPrimary;
        if (aPrimary && !bPrimary) return -1;
        if (!aPrimary && bPrimary) return 1;
        return (a.DisplayOrder ?? a.displayOrder ?? 0) - (b.DisplayOrder ?? b.displayOrder ?? 0);
      })
      .map((img) => ({
        id: img.ProductImageId ?? img.productImageId,
        url: getImageSrc(img.ImageUrl ?? img.imageUrl ?? ''),
        isPrimary: Boolean(img.IsPrimary ?? img.isPrimary),
      }))
      .filter((img) => img.url);
  } catch {
    return [];
  }
};

export const fetchProductImage = async (productId) => {
  const images = await fetchProductImages(productId);
  return images[0]?.url || '';
};

export const fetchProductAttributeValues = async (productId) => {
  const id = String(productId);
  for (const key of ['productId', 'ProductId']) {
    try {
      const params = new URLSearchParams({
        pageIndex: '1',
        pageSize: '100',
        search: '',
        sortColumn: 'AttributeValue',
        sortBy: 'ASC',
        [key]: id,
      });
      const { data } = await catalogApiFetch(`/ProductAttributeValue/GetAll?${params}`);
      const items = parseApiList(data);
      const forProduct = items.filter(
        (v) => Number(v.ProductId ?? v.productId) === Number(productId)
      );
      return forProduct.length > 0 ? forProduct : items;
    } catch {
      /* try next query param */
    }
  }
  return [];
};

export const fetchProductColors = async (productId) => {
  const items = await fetchProductAttributeValues(productId);
  return mapColorSwatchesFromAttributeValues(items);
};

export const mapProductToCard = async (p, { withColors = true, withVariant = true } = {}) => {
  const productId = p.ProductId;
  const [imageSrc, colors, productVariantId, reviewSummary] = await Promise.all([
    fetchProductImage(productId),
    withColors ? fetchProductColors(productId) : Promise.resolve([]),
    withVariant ? resolveProductVariantId(productId) : Promise.resolve(productId),
    fetchProductRatingSummary(productId),
  ]);
  const price = p.Price != null ? Number(p.Price) : 0;
  const originalPrice =
    p.OriginalPrice != null && Number(p.OriginalPrice) > price
      ? Number(p.OriginalPrice)
      : null;

  return {
    id: productId,
    productVariantId,
    name: p.ProductName || '',
    price,
    originalPrice,
    imageSrc: imageSrc || getImageSrc(p.PrimaryImageUrl || p.ImageUrl || ''),
    rating: reviewSummary.rating,
    reviewCount: reviewSummary.count,
    colors,
  };
};

export const fetchProductById = async (productId) => {
  const id = Number(productId);
  if (!Number.isFinite(id) || id <= 0) return null;

  try {
    const { data } = await catalogApiFetch(`/Product/GetById/${id}`);
    const product = data?.data ?? data;
    if (!product?.ProductId || !isActiveProduct(product)) return null;

    const [images, attrItems, productVariantId, reviewSummary] = await Promise.all([
      fetchProductImages(id),
      fetchProductAttributeValues(id),
      resolveProductVariantId(id),
      fetchProductRatingSummary(id),
    ]);

    const price = product.Price != null ? Number(product.Price) : 0;
    const originalPrice =
      product.OriginalPrice != null && Number(product.OriginalPrice) > price
        ? Number(product.OriginalPrice)
        : null;

    const colorOptions = parseColorOptions(attrItems);
    const fallbackImage = getImageSrc(product.PrimaryImageUrl || product.ImageUrl || '');
    const galleryImages = images.length
      ? images
      : fallbackImage
        ? [{ id: 0, url: fallbackImage, isPrimary: true }]
        : [];

    return {
      id,
      productVariantId,
      name: product.ProductName || '',
      price,
      originalPrice,
      sku: String(product.SKU ?? product.sku ?? '').trim(),
      brandName: product.BrandName ?? product.brandName ?? '',
      imageSrc: galleryImages[0]?.url || fallbackImage,
      images: galleryImages,
      sizes: parseSizeOptions(attrItems),
      colors: colorOptions,
      rating: reviewSummary.rating,
      reviewCount: reviewSummary.count,
    };
  } catch {
    return null;
  }
};

export const fetchProductsByCategory = async (categoryName) => {
  const params = new URLSearchParams({
    pageIndex: '1',
    pageSize: '200',
    search: '',
    sortColumn: 'ProductId',
    sortBy: 'DESC',
  });
  try {
    const { data } = await catalogApiFetch(`/Product/GetAll?${params}`);
    const items = parseApiList(data);
    return items.filter((p) => isActiveProduct(p) && matchesCategory(p, categoryName));
  } catch {
    return [];
  }
};
